import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./styles.css";
import { generatePuzzle } from "./SudokuLogic";
import { HelpModal } from "./HelpModal";

export enum SudokuLevel {
  easy = "easy",
  medium = "medium",
  hard = "hard",
}

const LEVELS: Record<SudokuLevel, { clues: number }> = {
  [SudokuLevel.easy]: {
    clues: 22,
  },
  [SudokuLevel.medium]: {
    clues: 17,
  },
  [SudokuLevel.hard]: {
    clues: 13,
  },
};

interface SudokuAppProps {
  level?: SudokuLevel;
}

type MessageState = {
  text: string;
  color: string;
};

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

const SudokuApp: React.FC<SudokuAppProps> = ({ level = SudokuLevel.easy }) => {
  const [board, setBoard] = useState<number[]>([]);
  const [solution, setSolution] = useState<number[]>([]);
  const [originalPuzzle, setOriginalPuzzle] = useState<number[]>([]);
  const [selectedCell, setSelectedCell] = useState<number | null>(null);
  const [selectedColor, setSelectedColor] = useState(1);
  const [errors, setErrors] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerStarted, setTimerStarted] = useState(false);
  const [gameFinished, setGameFinished] = useState(false);
  const [helpVisible, setHelpVisible] = useState(false);
  const [message, setMessage] = useState<MessageState | null>(null);
  const [wrongCells, setWrongCells] = useState<number[]>([]);

  const filledCells = useMemo(() => board.filter((value) => value !== 0).length, [board]);

  useEffect(() => {
    if (!timerStarted || gameFinished) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTimerSeconds((previous) => previous + 1);
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [timerStarted, gameFinished]);

  const clearMessage = () => setMessage(null);

  const showMessage = (text: string, color: string) => setMessage({ text, color });

  const resetGame = useCallback(() => {
    const generated = generatePuzzle(LEVELS[level].clues);

    setSolution(generated.solution);
    setOriginalPuzzle(generated.puzzle);
    setBoard([...generated.puzzle]);
    setSelectedCell(null);
    setSelectedColor(1);
    setErrors(0);
    setHintsUsed(0);
    setTimerSeconds(0);
    setTimerStarted(false);
    setGameFinished(false);
    setWrongCells([]);
    setMessage(null);
  }, [level]);

  useEffect(() => {
    resetGame();
  }, [resetGame]);

  const handleWin = (completedBoard = board) => {
    if (gameFinished) {
      return;
    }

    const complete = completedBoard.length === 36 && completedBoard.every((value, index) => value !== 0 && value === solution[index]);

    if (!complete) {
      return;
    }

    setGameFinished(true);
    setTimerStarted(false);
    showMessage("Молодец! Ты полностью решил судоку!", "#1a6a7c");
  };

  const checkGame = () => {
    if (gameFinished) {
      return;
    }

    let emptyCount = 0;
    const nextWrongCells: number[] = [];

    board.forEach((value, index) => {
      if (value === 0) {
        emptyCount += 1;
        return;
      }

      if (value !== solution[index]) {
        nextWrongCells.push(index);
      }
    });

    setWrongCells(nextWrongCells);

    if (nextWrongCells.length > 0) {
      showMessage("🔴 Некоторые цвета неправильные.", "#ef4444");
      return;
    }

    if (emptyCount > 0) {
      showMessage(`🟡 Осталось клеток: ${emptyCount}`, "#f59e0b");
      return;
    }

    handleWin();
  };

  const handleColorSelection = (color: number) => {
    if (gameFinished) {
      return;
    }

    setSelectedColor(color);

    if (selectedCell === null) {
      showMessage("👆 Сначала выбери клетку.", "#f59e0b");
      return;
    }

    if (originalPuzzle[selectedCell] !== 0) {
      showMessage("🔒 Эту клетку нельзя изменить.", "#f59e0b");
      return;
    }

    setTimerStarted(true);

    const nextBoard = [...board];
    nextBoard[selectedCell] = color;
    setBoard(nextBoard);

    if (color === solution[selectedCell]) {
      setWrongCells((current) => current.filter((cell) => cell !== selectedCell));
      clearMessage();
    } else {
      setErrors((currentErrors) => currentErrors + 1);
      setWrongCells((current) => Array.from(new Set([...current, selectedCell])));
      showMessage("❌ Неправильный цвет!", "#ef4444");
    }

    handleWin(nextBoard);
  };

  const handleHint = () => {
    if (gameFinished) {
      return;
    }

    setTimerStarted(true);

    const possibleIndexes: number[] = [];

    board.forEach((value, index) => {
      if (originalPuzzle[index] === 0 && value !== solution[index]) {
        possibleIndexes.push(index);
      }
    });

    if (possibleIndexes.length === 0) {
      checkGame();
      return;
    }

    const randomIndex = possibleIndexes[Math.floor(Math.random() * possibleIndexes.length)];
    const nextBoard = [...board];
    nextBoard[randomIndex] = solution[randomIndex];
    setBoard(nextBoard);
    setSelectedCell(randomIndex);
    setWrongCells((current) => current.filter((cell) => cell !== randomIndex));
    setHintsUsed((current) => current + 1);
    showMessage("💡 Правильный цвет открыт!", "#8b5cf6");

    handleWin(nextBoard);
  };

  const cellClassName = (index: number, value: number) => {
    const classes = ["sudoku-cell"];

    if (value > 0) {
      classes.push(`color-${value}`);
    }

    if (originalPuzzle[index] !== 0) {
      classes.push("fixed");
    }

    if (selectedCell === index) {
      classes.push("selected");
    }

    if (wrongCells.includes(index)) {
      classes.push("error");
    }

    return classes.join(" ");
  };

  return (
    <div className="sudoku-game">
      <div className="sudoku-shell">
        <header className="sudoku-top-bar">
          <a className="sudoku-logo-link" href="https://kotobot.app/index.html" aria-label="На главную">
            <img className="sudoku-logo" src="/img/kotobot.svg" alt="Kotobot logo" />
          </a>

          <div className="sudoku-header-center">
            <div className="sudoku-title-top">Судоку</div>
            <div className="sudoku-message-row">
              <div
                className="sudoku-message-bar"
                style={{
                  borderColor: message?.color ?? "#1a6a7c",
                  color: message?.color ?? "#1a6a7c",
                  boxShadow: message ? `inset 0 0 0 1px ${message.color}33` : "inset 0 0 0 1px rgba(26, 106, 124, 0.18)",
                }}
              >
                {message ? message.text : `Выбери клетку и начни игру`}
              </div>

              <img
                className="sudoku-help-icon"
                alt="Помощь"
                src="/img/help-fix.svg"
                title="Помощь"
                role="button"
                tabIndex={0}
                aria-haspopup="dialog"
                onClick={() => setHelpVisible(true)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setHelpVisible(true);
                  }
                }}
              />
            </div>
          </div>
        </header>

        <main>
          <div className="sudoku-board-layout">
            <aside className="sudoku-side-panel sudoku-side-left">
              <div className="sudoku-stat">
                <span className="sudoku-stat-label">Заполнено</span>
                <span className="sudoku-stat-value">{filledCells}/36</span>
              </div>

              <div className="sudoku-stat">
                <span className="sudoku-stat-label">Ошибки</span>
                <span className="sudoku-stat-value">{errors}</span>
              </div>

              <div className="sudoku-stat">
                <span className="sudoku-stat-label">Подсказки</span>
                <span className="sudoku-stat-value">{hintsUsed}</span>
              </div>

              <div className="sudoku-stat">
                <span className="sudoku-stat-label">Таймер</span>
                <span className="sudoku-stat-value">{formatTime(timerSeconds)}</span>
              </div>
            </aside>

            <div className="sudoku-grid-wrapper">
              <div className={`sudoku-board-flipper${gameFinished ? " is-flipped" : ""}`}>
                <div className="sudoku-grid sudoku-board-front" aria-hidden={gameFinished}>
                  {board.map((value, index) => (
                    <div
                      key={`${index}-${value}`}
                      className={cellClassName(index, value)}
                      onClick={() => {
                        if (gameFinished) {
                          return;
                        }

                        setSelectedCell(index);
                        clearMessage();
                      }}
                    />
                  ))}
                </div>
                <div className="sudoku-board-back" aria-hidden={!gameFinished}>
                  <div className="sudoku-victory-illustration">
                    <img src="/img/umnichka.svg" alt="Умничка!" />
                  </div>
                </div>
              </div>
            </div>

            <aside className="sudoku-side-panel sudoku-side-right">
              <div className="sudoku-palette">
                {[1, 2, 3, 4, 5, 6].map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`sudoku-color-button color-${color} ${selectedColor === color ? "active" : ""}`}
                    aria-label={`Цвет ${color}`}
                    onClick={() => handleColorSelection(color)}
                  />
                ))}
              </div>

              <div className="sudoku-instructions">Выбери клетку, затем выбери цвет.</div>

              <div className="sudoku-buttons">
                <button type="button" className="sudoku-action-button" onClick={checkGame}>
                  ✓ Проверить
                </button>

                <button type="button" className="sudoku-action-button" onClick={handleHint}>
                  💡 Подсказка
                </button>

                <button type="button" className="sudoku-action-button" onClick={resetGame}>
                  ↻ Заново
                </button>
              </div>
            </aside>
          </div>
        </main>
      </div>

      {helpVisible && <HelpModal onModalClose={() => setHelpVisible(false)} />}
    </div>
  );
};

export default SudokuApp;
